import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/api';
import UserSearchDropdown from '../../components/features/UserSearchDropdown';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorDisplay from '../../components/common/ErrorDisplay';
import SuccessBanner from '../../components/common/SuccessBanner';
import RichTextEditor from '../../components/common/RichTextEditor';
import BackButton from '../../components/common/BackButton';
import { stripHTML } from '../../utils/htmlSanitizer';
import './RequestCoin.css';

function RequestCoin() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [receiverId, setReceiverId] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendSuccess, setSendSuccess] = useState(false);

  // Fetch current user
  useEffect(() => {
    const fetchData = async () => {
      if (!auth.isAuthenticated || !auth.user?.id_token) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const idToken = auth.user.id_token;
        const userData = await apiService.getCurrentUser(idToken);
        setCurrentUser(userData);

        // Redirect organizations away from this page
        if (userData.user_type === 'organization') {
          navigate('/');
          return;
        }
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [auth.isAuthenticated, auth.user, navigate]);

  // Handle user selection from search dropdown
  const handleUserSelect = (user) => {
    setSelectedUser(user);
    if (user) {
      setReceiverId(user.user_id);
    } else {
      setReceiverId('');
    }
    setSendError(null);
  };

  // Submit request coin
  const handleSubmitRequest = async (e) => {
    e.preventDefault();

    if (!receiverId.trim()) {
      setSendError('Please select a person to request from');
      return;
    }

    const plainText = stripHTML(message).trim();
    if (!plainText) {
      setSendError('Please enter a message');
      return;
    }

    if (plainText.length > 1000) {
      setSendError('Message must be 1000 characters or less');
      return;
    }

    setSending(true);
    setSendError(null);

    try {
      const idToken = auth.user.id_token;

      // Coin type should always be 'individual' when requesting from a user
      // Only set to 'organizational' if requesting from an actual organization entity
      const requestedUserType = (selectedUser?.user_type === 'organization') ? 'organizational' : 'individual';

      console.log('REQUEST - Selected user:', selectedUser);
      console.log('REQUEST - User type:', selectedUser?.user_type);
      console.log('REQUEST - Coin type will be:', requestedUserType);

      // First create a coin with the message
      const newCoin = await apiService.createCoin(idToken, requestedUserType, message.trim());

      // Then create a pending request (sender is the requested party, receiver is current user)
      await apiService.requestCoin(idToken, newCoin.coin_id, receiverId.trim());

      setSendSuccess(true);

      // Reset form after 2 seconds
      setTimeout(() => {
        setReceiverId('');
        setSelectedUser(null);
        setMessage('');
        setSendSuccess(false);
        setSendError(null);
        setSending(false);
      }, 2000);
    } catch (err) {
      setSendError(err.message);
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="request-coin-container">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="request-coin-container">
        <ErrorDisplay
          title="Error Loading Page"
          message={error}
          onRetry={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className="request-coin-container">
      <BackButton />

      <div className="request-coin-header">
        <h1>Request a Coin</h1>
        <p className="header-description">
          Request recognition for your contributions from colleagues
        </p>
      </div>

      <div className="request-coin-card">
        {sendSuccess ? (
          <SuccessBanner
            message="Coin requested successfully!"
          />
        ) : (
          <form onSubmit={handleSubmitRequest} className="request-coin-form">
            <div className="form-row-container">
              <div className="form-group">
                <label htmlFor="receiverId">
                  Request From *
                </label>
                <UserSearchDropdown
                  onSelect={handleUserSelect}
                  placeholder="Search by name or email..."
                  debounceMs={500}
                  searchFn={async (query) => {
                    const idToken = auth.user.id_token;
                    return await apiService.searchUsers(idToken, query, 10);
                  }}
                  disabled={sending}
                />
                {selectedUser && (
                  <div className="selected-user-info">
                    <small>
                      Selected: <strong>{selectedUser.display_name}</strong> ({selectedUser.email})
                    </small>
                  </div>
                )}
                <small>
                  Search and select the person you want to request a coin from.
                </small>
              </div>

              {/* Coin type info - Organization coins temporarily disabled */}
              {/* <div className="coin-type-info">
                <div className="coin-type-info-row">
                  <span className="info-label">Coin Type:</span>
                  <div className="coin-type-badge">
                    <span className="badge-icon">
                      {selectedUser?.user_type === 'individual' ? '🪙' : '🏆'}
                    </span>
                    <span className="badge-text">
                      {selectedUser
                        ? (selectedUser.user_type === 'organization' ? 'Organizational' : 'Individual')
                        : 'Select a user first'
                      }
                    </span>
                  </div>
                </div>
                <small>
                  Coin type is automatically determined based on the requested party's account type
                </small>
              </div> */}
            </div>

            <div className="form-group">
              <label htmlFor="message">Message *</label>
              <RichTextEditor
                value={message}
                onChange={setMessage}
                placeholder="Describe what you'd like recognition for..."
                disabled={sending}
                maxLength={10000}
              />
            </div>

            {sendError && (
              <div className="error-message">
                {sendError}
              </div>
            )}

            <button
              type="submit"
              className="submit-button"
              disabled={sending}
            >
              {sending ? 'Requesting...' : 'Request Coin'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default RequestCoin;
